import React, { useState, useRef, useLayoutEffect } from 'react';

// Плавное раскрытие по ИЗМЕРЕННОЙ высоте (max-height в px). Контент раскладывается один раз,
// анимируется только высота контейнера → плавно даже с тяжёлым содержимым (sortable-уроки и т.п.),
// в отличие от grid-rows, который пересчитывает высоту контента каждый кадр.
//   keepMounted — не размонтировать контент при закрытии (нет затрат на маунт при открытии).
//   Когда полностью открыто — overflow:visible (перетаскивание не режется краем).
export default function Collapse({ open, children, duration = 280, keepMounted = false }) {
  const innerRef = useRef(null);
  const timer = useRef(null);
  const [mounted, setMounted] = useState(open || keepMounted);
  const [maxH, setMaxH] = useState(open ? 'none' : '0px');
  const first = useRef(true);

  useLayoutEffect(() => {
    if (first.current) { first.current = false; return; }
    clearTimeout(timer.current); cancelAnimationFrame(timer.current);
    if (open) {
      /* Контент монтируем сразу с нулевой высотой: если смонтировать его без
         ограничения, браузер успевает показать кадр с уже полным содержимым —
         и раскрытие выглядит рывком, особенно когда этот Collapse вложен в
         другой, уже открытый (номер внутри карточки работы). */
      if (!mounted) { setMaxH('0px'); setMounted(true); return; }
      /* Цель по высоте ведём всю анимацию: содержимое дорастает уже после
         первого замера (шрифты, картинки, вложенные блоки), и фиксированная
         высота давала рывок в конце. Ограничение снимаем сразу, как только
         анимация дошла до конца — без лишней паузы. */
      setMaxH('0px');                       // старт перехода всегда с нуля
      timer.current = requestAnimationFrame(() => {
        const started = performance.now();
        const follow = () => {
          const el = innerRef.current;
          if (!el) return;
          setMaxH(el.scrollHeight + 'px');
          /* Цель растёт по ходу анимации, поэтому переход финиширует позже, чем
             через duration от старта. Ждём, пока фактическая высота догонит
             цель, иначе снятие ограничения срезало хвост рывком. */
          const done = Math.abs(el.parentElement.offsetHeight - el.scrollHeight) < 2;
          if (!done && performance.now() - started < duration * 3) {
            timer.current = requestAnimationFrame(follow);
          } else {
            setMaxH('none');
          }
        };
        follow();
      });
    } else {
      const h = innerRef.current ? innerRef.current.scrollHeight : 0;
      setMaxH(h + 'px');                                          // фиксируем текущую высоту
      requestAnimationFrame(() => setMaxH('0px'));                // анимируем высота → 0
      if (!keepMounted) timer.current = setTimeout(() => setMounted(false), duration + 20);
    }
    return () => { clearTimeout(timer.current); cancelAnimationFrame(timer.current); };
  }, [open, mounted, keepMounted, duration]);

  /* Пока открыто — ограничение по высоте снимаем (maxHeight: none). Это важно
     для вложенных Collapse: если внутренний раскрывается внутри уже открытого
     внешнего, внешний не должен держать старую высоту — иначе содержимое
     выпрыгивает рывком, когда ограничение наконец снимут. */
  return (
    <div style={{ maxHeight: maxH, overflow: maxH === 'none' ? 'visible' : 'hidden', willChange: maxH === 'none' ? undefined : 'max-height', transition: maxH === 'none' ? undefined : `max-height ${duration}ms cubic-bezier(.33,0,.2,1)` }}>
      <div ref={innerRef}>{(mounted || keepMounted) ? children : null}</div>
    </div>
  );
}
